import { useMemo, useState } from 'react';
import { GridColDef } from '@mui/x-data-grid';
import { DateTooltip } from '../../../../components/general/dateTooltip';
import { TableTooltip } from '../../../../components/general/table';
import {
    IconButton,
    Tooltip,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    Box,
    Typography,
    Divider,
} from '@mui/material';
import VisibilityIcon from '@mui/icons-material/Visibility';
import CloseIcon from '@mui/icons-material/Close';

interface UseSurveyResponseLogsProps {
    historical: any[];
}

// Componente para mostrar el detalle de respuestas con comparación
const ResponseDetailDialog = ({
    open,
    onClose,
    oldResponses,
    newResponses,
}: {
    open: boolean;
    onClose: () => void;
    oldResponses: any[] | null;
    newResponses: any[];
}) => {
    if (!newResponses || newResponses.length === 0) {
        return null;
    }

    // Si hay oldResponses, mostrar solo las diferencias (respuestas nuevas o modificadas)
    const isUpdate =
        oldResponses && Array.isArray(oldResponses) && oldResponses.length > 0;

    let responsesToShow = newResponses;
    let title = `Detalle de Respuestas (${newResponses.length})`;

    if (isUpdate) {
        // Encontrar respuestas que son nuevas o diferentes
        const oldResponsesMap = new Map(
            oldResponses.map((r) => [r.questionText || r.question_id, r])
        );

        responsesToShow = newResponses.filter((newResp) => {
            const oldResp = oldResponsesMap.get(
                newResp.questionText || newResp.question_id
            );

            // Si no existía antes, es nueva
            if (!oldResp) return true;

            // Si cambió la respuesta o el why, incluirla
            return (
                oldResp.answerText !== newResp.answerText ||
                oldResp.why !== newResp.why
            );
        });

        title = `Respuestas Agregadas/Modificadas (${responsesToShow.length} de ${newResponses.length})`;
    }

    return (
        <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
            <DialogTitle>
                {title}
                <IconButton
                    aria-label="cerrar"
                    onClick={onClose}
                    sx={{
                        position: 'absolute',
                        right: 8,
                        top: 8,
                        color: (theme) => theme.palette.grey[500],
                    }}
                >
                    <CloseIcon />
                </IconButton>
            </DialogTitle>
            <DialogContent dividers>
                {responsesToShow.length === 0 ? (
                    <Typography variant="body2" color="text.secondary">
                        No hay cambios en las respuestas
                    </Typography>
                ) : (
                    responsesToShow.map((response, index) => (
                        <Box key={index} sx={{ mb: 3 }}>
                            <Typography
                                variant="subtitle2"
                                color="primary"
                                gutterBottom
                            >
                                Respuesta {index + 1}
                            </Typography>
                            {response.questionText && (
                                <Box sx={{ mb: 1 }}>
                                    <Typography
                                        variant="caption"
                                        color="text.secondary"
                                    >
                                        Pregunta:
                                    </Typography>
                                    <Typography variant="body2">
                                        {response.questionText}
                                    </Typography>
                                </Box>
                            )}
                            {response.answerText && (
                                <Box sx={{ mb: 1 }}>
                                    <Typography
                                        variant="caption"
                                        color="text.secondary"
                                    >
                                        Respuesta:
                                    </Typography>
                                    <Typography variant="body2">
                                        {response.answerText}
                                    </Typography>
                                </Box>
                            )}
                            {response.why && (
                                <Box sx={{ mb: 1 }}>
                                    <Typography
                                        variant="caption"
                                        color="text.secondary"
                                    >
                                        Razón:
                                    </Typography>
                                    <Typography variant="body2">
                                        {response.why}
                                    </Typography>
                                </Box>
                            )}
                            {index < responsesToShow.length - 1 && (
                                <Divider sx={{ mt: 2 }} />
                            )}
                        </Box>
                    ))
                )}
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose}>Cerrar</Button>
            </DialogActions>
        </Dialog>
    );
};

// Función para formatear valores complejos
const formatValue = (value: any, field?: string): string => {
    if (value === null || value === undefined || value === '') {
        return '-';
    }

    if (typeof value === 'boolean') {
        return value ? 'Sí' : 'No';
    }

    // Formateo especial para el campo status
    if (field === 'status') {
        if (value === 'completed') return 'Completada';
        if (value === 'incomplete') return 'Incompleta';
        return String(value);
    }

    if (typeof value === 'object') {
        // Si es un array de respuestas
        if (Array.isArray(value)) {
            if (value.length === 0) return 'Sin respuestas';

            const count = value.length;
            return `${count} respuesta${count !== 1 ? 's' : ''} (Ver detalle)`;
        }

        // Si es un objeto simple
        try {
            const obj = value as any;
            if (obj.questionText || obj.answerText) {
                const parts = [];
                if (obj.questionText)
                    parts.push(`Pregunta: ${obj.questionText}`);
                if (obj.answerText) parts.push(`Respuesta: ${obj.answerText}`);
                return parts.join('\n');
            }

            const formatted = JSON.stringify(value, null, 2);
            return formatted.length > 150
                ? formatted.substring(0, 150) + '...'
                : formatted;
        } catch {
            return String(value);
        }
    }

    return String(value);
};

// Función para traducir nombres de campos
const translateField = (field: string): string => {
    const translations: { [key: string]: string } = {
        status: 'Estado',
        responses: 'Respuestas',
        caller_id: 'Caller ID',
        document_number: 'Número de Documento',
        customer_name: 'Nombre del Cliente',
        associated_service: 'Servicio Asociado',
        completed_at: 'Fecha de Finalización',
        created_at: 'Fecha de Creación',
        updated_at: 'Fecha de Actualización',
    };

    return translations[field] || field;
};

export const useSurveyResponseLogs = ({
    historical,
}: UseSurveyResponseLogsProps) => {
    const [detailDialog, setDetailDialog] = useState<{
        open: boolean;
        oldResponses: any[] | null;
        newResponses: any[];
    }>({
        open: false,
        oldResponses: null,
        newResponses: [],
    });

    const handleOpenDetail = (
        oldResponses: any[] | null,
        newResponses: any[]
    ) => {
        setDetailDialog({ open: true, oldResponses, newResponses });
    };

    const handleCloseDetail = () => {
        setDetailDialog({ open: false, oldResponses: null, newResponses: [] });
    };

    const columns: GridColDef[] = useMemo(
        () => [
            {
                field: 'created_at',
                headerName: 'Fecha',
                flex: 0.8,
                minWidth: 160,
                renderCell: (params) => (
                    <DateTooltip date={params.row.created_at} showFornow />
                ),
            },
            {
                field: 'createdBy',
                headerName: 'Usuario',
                flex: 0.8,
                minWidth: 140,
                renderCell: (params) => {
                    const user = params.row.createdBy;
                    if (!user) return <TableTooltip value="-" />;

                    const username = user.username || '';
                    const name = user.name || '';
                    const surname = user.surname || '';

                    const fullName = username
                        ? `(${username}) ${name} ${surname}`.trim()
                        : `${name} ${surname}`.trim() || '-';

                    return <TableTooltip value={fullName} />;
                },
            },
            {
                field: 'field',
                headerName: 'Campo',
                flex: 0.6,
                minWidth: 120,
                renderCell: (params) => {
                    const field = params.row.elementos?.field || '-';
                    return <TableTooltip value={translateField(field)} />;
                },
            },
            {
                field: 'oldValue',
                headerName: 'Valor Anterior',
                flex: 1.2,
                minWidth: 200,
                renderCell: (params) => {
                    const value = params.row.elementos?.oldValue;
                    const field = params.row.elementos?.field;

                    if (value === null || value === undefined) {
                        return <TableTooltip value="(Registro creado)" />;
                    }

                    // Si es un array de respuestas, mostrar con botón de detalle
                    if (Array.isArray(value) && value.length > 0) {
                        const newValue = params.row.elementos?.newValue;
                        const newResponses = Array.isArray(newValue)
                            ? newValue
                            : [];

                        return (
                            <Box
                                sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 1,
                                }}
                            >
                                <Typography variant="body2">
                                    {formatValue(value, field)}
                                </Typography>
                                <Tooltip title="Ver detalle de respuestas">
                                    <IconButton
                                        size="small"
                                        onClick={() =>
                                            handleOpenDetail(
                                                value,
                                                newResponses
                                            )
                                        }
                                        sx={{ p: 0.5 }}
                                    >
                                        <VisibilityIcon fontSize="small" />
                                    </IconButton>
                                </Tooltip>
                            </Box>
                        );
                    }

                    const formatted = formatValue(value, field);
                    return <TableTooltip value={formatted} />;
                },
            },
            {
                field: 'newValue',
                headerName: 'Valor Nuevo',
                flex: 1.2,
                minWidth: 200,
                renderCell: (params) => {
                    const value = params.row.elementos?.newValue;
                    const field = params.row.elementos?.field;

                    // Si es un array de respuestas, mostrar con botón de detalle
                    if (Array.isArray(value) && value.length > 0) {
                        const oldValue = params.row.elementos?.oldValue;
                        const oldResponses = Array.isArray(oldValue)
                            ? oldValue
                            : null;

                        return (
                            <Box
                                sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 1,
                                }}
                            >
                                <Typography variant="body2">
                                    {formatValue(value, field)}
                                </Typography>
                                <Tooltip title="Ver detalle de respuestas">
                                    <IconButton
                                        size="small"
                                        onClick={() =>
                                            handleOpenDetail(
                                                oldResponses,
                                                value
                                            )
                                        }
                                        sx={{ p: 0.5 }}
                                    >
                                        <VisibilityIcon fontSize="small" />
                                    </IconButton>
                                </Tooltip>
                            </Box>
                        );
                    }

                    const formatted = formatValue(value, field);
                    return <TableTooltip value={formatted} />;
                },
            },
        ],
        [handleOpenDetail]
    );

    const rows = useMemo(() => {
        if (!historical || !Array.isArray(historical)) return [];

        return historical.flatMap((row) => {
            if (row.changes) {
                const { changes, created_at, createdBy } = row;

                const changesArray = Array.isArray(changes)
                    ? changes
                    : Object.entries(changes).map(
                        ([field, change]: [string, any]) => ({
                            field,
                            oldValue: change.from,
                            newValue: change.to,
                        })
                    );

                return changesArray.map((elemento, index) => {
                    return {
                        ...row,
                        elementos: {
                            field: elemento.field,
                            newValue: elemento.newValue,
                            oldValue: elemento.oldValue,
                        },
                        id: row.id + '-' + index,
                        created_at,
                        createdBy,
                    };
                });
            }
            return [];
        });
    }, [historical]);

    return {
        columns,
        rows,
        detailDialog: (
            <ResponseDetailDialog
                open={detailDialog.open}
                onClose={handleCloseDetail}
                oldResponses={detailDialog.oldResponses}
                newResponses={detailDialog.newResponses}
            />
        ),
    };
};
